<template>
    <div class="p-4 max-w-5xl mx-auto">
        <div class="flex justify-between items-center mb-4">
            <h1 class="text-2xl font-semibold">Warranties</h1>
            <div class="flex space-x-2">
                <button type="button" @click="openCreate" class="rounded px-3 py-1 bg-dark-primary text-white hover:bg-dark-secondary">+ New warranty</button>
            </div>
        </div>

        <div class="bg-gray-50 border rounded p-3 mb-4">
            <div class="flex items-center justify-between">
                <div class="text-sm">
                    <strong>Expiring-soon threshold:</strong>
                    <span v-if="!editingThreshold">{{ threshold.expiring_soon_days }} days</span>
                    <input v-else type="number" min="1" v-model.number="thresholdDraft" class="border rounded p-1 w-20 ml-1" />
                </div>
                <div>
                    <button v-if="!editingThreshold" @click="editThreshold" class="text-sm text-accent-primary hover:underline">edit</button>
                    <span v-else>
                        <button @click="saveThreshold" class="text-sm text-accent-primary hover:underline mr-2">save</button>
                        <button @click="editingThreshold = false" class="text-sm text-gray-500 hover:underline">cancel</button>
                    </span>
                </div>
            </div>
            <div class="text-xs text-gray-500 mt-1">
                Drives the "expiring soon" chip on items. Default 60 days.
            </div>
        </div>

        <div v-if="loading" class="text-sm text-gray-500 italic">Loading…</div>
        <div v-else-if="!warranties.length" class="text-sm text-gray-500 italic">No warranties yet.</div>
        <table v-else class="w-full text-sm border-collapse">
            <thead class="text-left text-xs uppercase text-gray-500 border-b">
                <tr>
                    <th class="py-2">Name</th>
                    <th class="py-2">Provider</th>
                    <th class="py-2">Contract #</th>
                    <th class="py-2">Default end</th>
                    <th class="py-2">Items</th>
                    <th class="py-2">Status</th>
                    <th class="py-2"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="w in warranties" :key="w.id" class="border-b hover:bg-gray-50">
                    <td class="py-2 font-medium">{{ w.name }}</td>
                    <td class="py-2">{{ w.provider || '—' }}</td>
                    <td class="py-2">{{ w.contract_number || '—' }}</td>
                    <td class="py-2">{{ w.default_end || '—' }}</td>
                    <td class="py-2">{{ w.item_count }}</td>
                    <td class="py-2">
                        <span class="text-xs px-2 py-0.5 rounded" :class="statusBadgeClass(w.derived_status)">{{ w.derived_status }}</span>
                    </td>
                    <td class="py-2">
                        <button @click="openEdit(w)" class="text-xs text-accent-primary hover:underline mr-2">edit</button>
                        <button @click="deleteOne(w)" class="text-xs text-red-600 hover:underline" :disabled="w.item_count > 0" :title="w.item_count > 0 ? 'Cannot delete: items still reference this warranty' : 'Delete'">delete</button>
                    </td>
                </tr>
            </tbody>
        </table>

        <base-modal-small v-if="formOpen" @close="formOpen = false">
            <template #header>{{ form.id ? 'Edit warranty' : 'New warranty' }}</template>
            <template #body>
                <div class="space-y-2 text-sm p-2">
                    <div class="flex flex-col">
                        <label class="text-gray-500">Name*</label>
                        <input type="text" v-model="form.name" required class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Provider</label>
                        <input type="text" v-model="form.provider" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Contract number</label>
                        <input type="text" v-model="form.contract_number" class="border rounded p-1" />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Coverage terms</label>
                        <textarea v-model="form.coverage_terms" class="border rounded p-1"></textarea>
                    </div>
                    <div class="flex space-x-2">
                        <div class="flex flex-col flex-1">
                            <label class="text-gray-500">Default start</label>
                            <input type="date" v-model="form.default_start" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col flex-1">
                            <label class="text-gray-500">Default end</label>
                            <input type="date" v-model="form.default_end" class="border rounded p-1" />
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <label class="text-gray-500">Notes</label>
                        <textarea v-model="form.notes" class="border rounded p-1"></textarea>
                    </div>
                    <div class="flex justify-end space-x-2 pt-2">
                        <button type="button" @click="formOpen = false" class="px-3 py-1 border rounded">Cancel</button>
                        <button type="button" :disabled="!form.name || busy" @click="submit" class="px-3 py-1 rounded bg-dark-primary text-white">{{ form.id ? 'Save' : 'Create' }}</button>
                    </div>
                </div>
            </template>
        </base-modal-small>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import {
    listWarranties, createWarranty, updateWarranty, deleteWarranty,
    getWarrantyThresholdSettings, updateWarrantyThresholdSettings,
} from '@/services/Inventory';

const store = useStore();
const $toast = useToast();

const warranties = ref([]);
const loading = ref(false);
const busy = ref(false);
const formOpen = ref(false);
const form = reactive({
    id: null, name: '', provider: '', contract_number: '',
    coverage_terms: '', default_start: null, default_end: null, notes: '',
});

const threshold = ref({ expiring_soon_days: 60 });
const editingThreshold = ref(false);
const thresholdDraft = ref(60);

function statusBadgeClass(s) {
    return {
        active: 'bg-green-100 text-green-800',
        expiring_soon: 'bg-amber-100 text-amber-800',
        expired: 'bg-red-100 text-red-800',
        unknown: 'bg-gray-200 text-gray-700',
    }[s] || 'bg-gray-100 text-gray-700';
}

async function load() {
    loading.value = true;
    try {
        const [wResp, tResp] = await Promise.all([
            listWarranties(store.state.auth.token, '?page_size=500'),
            getWarrantyThresholdSettings(store.state.auth.token),
        ]);
        warranties.value = wResp.data?.results || wResp.data || [];
        threshold.value = tResp.data;
    } catch (e) {
        $toast.error(`Could not load warranties: ${describe(e)}`);
    } finally {
        loading.value = false;
    }
}

onMounted(load);

function openCreate() {
    Object.assign(form, {
        id: null, name: '', provider: '', contract_number: '',
        coverage_terms: '', default_start: null, default_end: null, notes: '',
    });
    formOpen.value = true;
}

function openEdit(w) {
    Object.assign(form, {
        id: w.id, name: w.name, provider: w.provider || '',
        contract_number: w.contract_number || '',
        coverage_terms: w.coverage_terms || '',
        default_start: w.default_start || null,
        default_end: w.default_end || null,
        notes: w.notes || '',
    });
    formOpen.value = true;
}

async function submit() {
    busy.value = true;
    try {
        const token = store.state.auth.token;
        const payload = { ...form };
        delete payload.id;
        Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
        if (form.id) {
            await updateWarranty(form.id, payload, token);
            $toast.success('Warranty updated.');
        } else {
            await createWarranty(payload, token);
            $toast.success('Warranty created.');
        }
        formOpen.value = false;
        await load();
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

async function deleteOne(w) {
    if (w.item_count > 0) {
        $toast.error("Can't delete: items still reference this warranty.");
        return;
    }
    if (!confirm(`Delete warranty "${w.name}"?`)) return;
    busy.value = true;
    try {
        await deleteWarranty(w.id, store.state.auth.token);
        $toast.success('Warranty deleted.');
        await load();
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

function editThreshold() {
    thresholdDraft.value = threshold.value.expiring_soon_days;
    editingThreshold.value = true;
}

async function saveThreshold() {
    busy.value = true;
    try {
        const resp = await updateWarrantyThresholdSettings(
            { expiring_soon_days: thresholdDraft.value },
            store.state.auth.token,
        );
        threshold.value = resp.data;
        editingThreshold.value = false;
        $toast.success('Threshold saved.');
        await load();
    } catch (e) {
        $toast.error(describe(e));
    } finally {
        busy.value = false;
    }
}

function describe(e) {
    if (e?.response?.data) {
        if (typeof e.response.data === 'string') return e.response.data;
        return Object.values(e.response.data).flat().join(' · ');
    }
    return e?.message || String(e);
}
</script>
